import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = '/api/shop';

const DURATION_LABELS = { WEEK: 'Woche', MONTH: 'Monat', YEAR: 'Jahr', UNLIMITED: 'Unbegrenzt' };

function Shop() {
  const [shopData, setShopData] = useState(null);  // { programName, groups }
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [programName, setProgramName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const [configured, setConfigured] = useState(null);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [activatingFree, setActivatingFree] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paypalRef = useRef(null);
  const scriptRef = useRef(null);
  const cancelled = searchParams.get('cancelled');

  const loadPayPalScript = useCallback((clientId) => {
    if (scriptRef.current) return;
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=EUR&locale=de_DE`;
    script.async = true;
    script.onload = () => setPaypalLoaded(true);
    scriptRef.current = script;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const [configRes, productsRes] = await Promise.all([
          fetch(`${API}/config`),
          fetch(`${API}/products`),
        ]);
        const configData = await configRes.json();
        const productsData = await productsRes.json();

        setConfigured(configData.configured);
        setProgramName(configData.programName || 'Familytool');
        setShopData(productsData);

        if (configData.configured && configData.clientId) {
          loadPayPalScript(configData.clientId);
        }

        // Auto-select first highlighted product or first product overall
        const firstGroup = productsData.groups?.[0];
        if (firstGroup?.products?.length) {
          const highlighted = firstGroup.products.find(p => p.highlight) || firstGroup.products[0];
          setSelectedProduct(highlighted);
        }
      } catch (e) {
        setError('Fehler beim Laden der Shop-Daten.');
      }
    }
    init();
  }, [loadPayPalScript]);

  // When group tab changes: auto-select first highlighted product in that group
  const handleGroupChange = (idx) => {
    setActiveGroupIdx(idx);
    const group = shopData?.groups?.[idx];
    if (group?.products?.length) {
      const highlighted = group.products.find(p => p.highlight) || group.products[0];
      setSelectedProduct(highlighted);
    }
  };

  // Render PayPal buttons when ready
  useEffect(() => {
    if (!paypalLoaded || !selectedProduct || !window.paypal) return;
    const container = paypalRef.current;
    if (!container) return;
    container.innerHTML = '';

    window.paypal.Buttons({
      style: { layout: 'vertical', color: 'black', shape: 'rect', label: 'pay', height: 48 },

      createOrder: async () => {
        setError('');
        if (!email || !email.includes('@')) {
          setEmailError('Bitte gib eine gültige E-Mail-Adresse ein.');
          throw new Error('No email');
        }
        setEmailError('');

        const body = typeof selectedProduct.id === 'number'
          ? { product_id: selectedProduct.id, email }
          : { plan: selectedProduct.id, email };

        const r = await fetch(`${API}/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || 'Order creation failed');
        return data.orderId;
      },

      onApprove: async (data) => {
        const body = typeof selectedProduct.id === 'number'
          ? { orderId: data.orderID, email, product_id: selectedProduct.id }
          : { orderId: data.orderID, email, plan: selectedProduct.id };

        const r = await fetch(`${API}/capture-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await r.json();
        if (!r.ok) { setError(result.detail || 'Fehler beim Abschließen der Zahlung.'); return; }

        const params = new URLSearchParams({
          key: result.licenseKey,
          email: result.email,
          plan: result.plan,
          expires: result.expiresAt || '',
        });
        navigate(`/shop/success?${params.toString()}`);
      },

      onError: (err) => {
        console.error(err);
        setError('PayPal-Fehler: ' + (err.message || 'Unbekannter Fehler'));
      },

      onCancel: () => setError('Zahlung abgebrochen. Du kannst es erneut versuchen.'),
    }).render(container);
  }, [paypalLoaded, selectedProduct, email, navigate]);

  const handleActivateFree = async () => {
    setError('');
    if (!email || !email.includes('@')) {
      setEmailError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    setEmailError('');
    setActivatingFree(true);
    try {
      const r = await fetch(`${API}/activate-free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: selectedProduct.id, email }),
      });
      const result = await r.json();
      if (!r.ok) { setError(result.detail || 'Fehler bei der Aktivierung.'); return; }

      const params = new URLSearchParams({
        key: result.licenseKey,
        email: result.email,
        plan: result.plan,
        expires: result.expiresAt || '',
      });
      navigate(`/shop/success?${params.toString()}`);
    } catch (e) {
      setError('Fehler bei der Aktivierung.');
    } finally {
      setActivatingFree(false);
    }
  };

  const activeGroup = shopData?.groups?.[activeGroupIdx];
  const multipleGroups = (shopData?.groups?.length || 0) > 1;

  const durationLabel = selectedProduct
    ? DURATION_LABELS[selectedProduct.duration_type] || selectedProduct.duration_type
    : '';

  if (!shopData) {
    return (
      <div className="shop-page">
        {error
          ? <div className="alert alert-error">{error}</div>
          : <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>Lädt…</div>
        }
      </div>
    );
  }

  return (
    <div className="shop-page">
      {/* Hero */}
      <div className="shop-header">
        <div className="shop-header-badge">Premium</div>
        <h1>{programName}</h1>
        <p>Schalte alle Funktionen frei — sofortige Aktivierung nach dem Kauf.</p>
      </div>

      {cancelled && (
        <div className="alert alert-warning" style={{ marginBottom: '24px' }}>
          Zahlung abgebrochen. Du kannst es erneut versuchen.
        </div>
      )}

      {/* Group tabs (only when multiple groups) */}
      {multipleGroups && (
        <div className="shop-tabs">
          {shopData.groups.map((group, idx) => (
            <button
              key={group.id ?? idx}
              className={`shop-tab ${activeGroupIdx === idx ? 'active' : ''}`}
              onClick={() => handleGroupChange(idx)}
            >
              {group.name}
            </button>
          ))}
        </div>
      )}

      {/* Group description */}
      {activeGroup?.description && (
        <p className="shop-group-desc">{activeGroup.description}</p>
      )}

      {/* Product cards */}
      <div className="plans-grid">
        {(activeGroup?.products || []).map(product => (
          <div
            key={product.id}
            className={`plan-card ${product.highlight ? 'highlight' : ''} ${selectedProduct?.id === product.id ? 'selected' : ''}`}
            onClick={() => setSelectedProduct(product)}
          >
            {product.badge && (
              <div className="plan-badge-wrap">
                <span className="plan-badge">{product.badge}</span>
              </div>
            )}
            <div className="plan-radio" />
            <div className="plan-name">{product.name}</div>
            <div className="plan-price">
              {product.price.toFixed(2).replace('.', ',')} {product.currency}
              <span> / {DURATION_LABELS[product.duration_type] || product.duration_type}</span>
            </div>
            {product.description && (
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                {product.description}
              </p>
            )}
            {product.features?.length > 0 && (
              <ul className="plan-features">
                {product.features.map(f => <li key={f} className="plan-feature">{f}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Checkout */}
      <div className="checkout-card">
        <h3>Bestellung abschließen</h3>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-group">
          <label className="form-label" htmlFor="shop-email">E-Mail-Adresse</label>
          <input
            type="email" id="shop-email"
            className={`form-input ${emailError ? 'error' : ''}`}
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
            placeholder="deine@email.de"
            autoComplete="email"
          />
          {emailError && <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--red)' }}>{emailError}</p>}
        </div>

        {selectedProduct && (
          <div className="order-summary">
            <div className="summary-row">
              <span>Produkt</span>
              <span>{multipleGroups ? `${activeGroup?.name} – ` : ''}{selectedProduct.name}</span>
            </div>
            <div className="summary-row">
              <span>Laufzeit</span>
              <span>{durationLabel}</span>
            </div>
            <div className="summary-row total">
              <span>Gesamt</span>
              <span>{selectedProduct.price.toFixed(2).replace('.', ',')} {selectedProduct.currency}</span>
            </div>
          </div>
        )}

        {selectedProduct && Number(selectedProduct.price) === 0 ? (
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={activatingFree}
            onClick={handleActivateFree}
          >
            {activatingFree ? 'Aktiviere…' : 'Kostenlos aktivieren'}
          </button>
        ) : (
          <>
            {configured === false && (
              <div className="not-configured-notice">
                PayPal ist noch nicht konfiguriert. Bitte trage die API-Zugangsdaten ein.
              </div>
            )}

            {configured === true && (
              <div className="paypal-wrap" ref={paypalRef} />
            )}
          </>
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-dim)', marginTop: '20px' }}>
        Der Lizenzschlüssel wird nach dem Kauf an deine E-Mail-Adresse gesendet.
      </p>
    </div>
  );
}

export default Shop;
